import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  AmbientLight,
  DirectionalLight,
} from 'three';

export interface SceneResources {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  domElement: HTMLElement;
  dispose: () => void;
}

export interface SceneSetupOptions {
  backgroundVideoUrl?: string;
  /** When false, the in-scene breathing ring will not be created/updated. */
  showBreathRing?: boolean;
}

// Create the core Three.js scene, camera and renderer and attach the
// canvas to the supplied container element. The returned object contains
// a `dispose` helper that will cleanup renderer and event listeners.
export default function setupScene(
  container: HTMLElement,
  _options?: SceneSetupOptions
): SceneResources {
  const width = Math.max(0, container.clientWidth);
  const height = Math.max(0, container.clientHeight || window.innerHeight);

  const scene = new Scene();

  const camera = new PerspectiveCamera(45, width / height, 0.1, 1000);
  camera.position.set(0, 1.5, 4);
  camera.lookAt(0, 1, 0);

  const ambientLight = new AmbientLight(0xffffff, 0.8);
  scene.add(ambientLight);

  const directionalLight = new DirectionalLight(0xfff4d2, 1.15);
  directionalLight.position.set(4, 6, 4);
  directionalLight.target.position.set(0, 1.2, 0);
  scene.add(directionalLight);
  scene.add(directionalLight.target);

  let renderer: WebGLRenderer;
  try {
    renderer = new WebGLRenderer({ antialias: true, alpha: true });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('setupScene: failed to create WebGLRenderer', e);
    throw e;
  }

  const gl = renderer.getContext();
  if (!gl) {
    // eslint-disable-next-line no-console
    console.error(
      'setupScene: WebGL context unavailable (blocked or unsupported)'
    );
    try {
      renderer.dispose();
    } catch {
      /* ignore */
    }
    throw new Error('WebGL context unavailable');
  }

  renderer.setSize(width, height, false);
  renderer.setPixelRatio(window.devicePixelRatio || 1);
  renderer.setClearColor(0x000000, 0);

  // attach the canvas
  const dom = renderer.domElement;
  // Ensure the canvas fills the viewport and doesn't shrink on mobile; use absolute viewport units
  dom.style.width = '100vw';
  dom.style.height = '100vh';
  dom.style.minWidth = '100vw';
  dom.style.minHeight = '100vh';
  dom.style.display = 'block';
  dom.setAttribute('aria-hidden', 'true');
  const handleContextLost = (event: Event) => {
    event.preventDefault();
    // eslint-disable-next-line no-console
    console.warn('setupScene: WebGL context lost; preventing default');
  };

  const handleContextRestored = () => {
    // eslint-disable-next-line no-console
    console.info('setupScene: WebGL context restored');
  };

  dom.addEventListener('webglcontextlost', handleContextLost, false);
  dom.addEventListener('webglcontextrestored', handleContextRestored, false);
  container.appendChild(dom);

  const onResize = () => {
    const w = Math.max(0, container.clientWidth);
    const h = Math.max(0, container.clientHeight || window.innerHeight);
    camera.aspect = w / h || 1;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  };

  window.addEventListener('resize', onResize);

  const dispose = () => {
    window.removeEventListener('resize', onResize);
    try {
      if (dom) {
        dom.removeEventListener('webglcontextlost', handleContextLost);
        dom.removeEventListener('webglcontextrestored', handleContextRestored);
      }
      if (dom && dom.parentElement) dom.parentElement.removeChild(dom);
    } catch {
      /* ignore */
    }
    try {
      renderer.dispose();
    } catch {
      /* ignore */
    }
    try {
      scene.remove(ambientLight);
      scene.remove(directionalLight);
      scene.remove(directionalLight.target);
    } catch {
      /* ignore */
    }
  };

  return { scene, camera, renderer, domElement: dom, dispose };
}
